import parseStoreHours from "./parse-store-hours";
import trapFocus from "./trap-focus";
import fetchStoresData from "./fetch-stores-data";

/**
 * Store Landing
 * @param {object} opts
 */
function storeLandingPage(opts) {
  const config = {
    dom: {
      buttonsFilter: 'js-store-navigation-buttons',
      button: 'js-store-navigation-button',
      dropdownHeading: 'dropdown-heading',
      dropdownItem: 'js-dropdown-list-item',
      dropdownMenu: 'js-options-dropdown-menu',
      dropdownWrapper: 'dropdownWrapper',
      dropdownFilter: 'js-store-navigation-dropdown',
      dropdownOption: 'js-store-navigation-option',
      filterOption: 'js-store-filter-option',
      filterParent: 'js-store-filter-parent',
      labelButton: 'dropdown-list__item-btn',
      labelText: 'js-options-trigger-text',
      main: 'storeLanding',
      nearestStoreLink: 'js-nearest-store',
      nearestStoreText: 'js-nearest-store-text',
      nearestStoreSubheaderWrapper: 'js-nearest-store-subheader-wrapper',
      nearestStoreWrapper: 'js-nearest-store-wrapper',
      noStore: 'js-no-store',
      noStoreText: 'js-no-store-text',
      optionsDropdownList: 'js-options-dropdown-list',
      optionsLabelIcon: 'options-label__icon',
      optionsTrigger: 'js-options-trigger',
      wrapper: 'js-store-grid',
    },
    cls: {
      active: 'is-active',
      hide: 'hide',
      hover: 'is-hovered',
      open: 'is-open',
      loading: 'is-loading',
      optionSelected: 'option-selected',
    },
  };
  const c = __.extend(config, opts);

  // global elements
  const $main = document.getElementById(c.dom.main);
  const $buttonsFilter = $main.querySelector(`.${c.dom.buttonsFilter}`);
  const $dropdownFilter = $main.querySelector(`.${c.dom.dropdownMenu}`);
  const $optionsDropdownList = $main.querySelector(`.${c.dom.optionsDropdownList}`);
  const $dropdownWrapper = document.getElementById(c.dom.dropdownWrapper);
  const $selectCustomOpts = document.querySelectorAll(`.${c.dom.dropdownItem}`);
  const $section = $dropdownWrapper?.closest('section').parentNode;
  const $labelTextElement = $section?.querySelector(`.${c.dom.labelText}`);
  const $optionsTrigger = $section?.querySelector(`.${c.dom.optionsTrigger}`);
  const stateFromUrl = getStateFromUrl();
  let $buttons;
  let $dropdownOptions;
  let currentState;

  // global data
  const storage = window.sessionStorage;
  const touts = SDG.Data.storeLandingTouts || null;
  // `nearestStores`: snapshot from sessionStorage, populated by other surfaces
  // (footer, BOPIS, etc.). It excludes "Coming Soon" stores and is only used
  // to know which store to pin to the top of the grid as "Your Nearest Store".
  const nearestStoresJson = storage?.getItem('nearestStoresData');
  const nearestStores = nearestStoresJson ? JSON.parse(nearestStoresJson) : null;
  let nearestStore = nearestStores ? nearestStores[0] : null;
  // `proximitySortedStores`: full Stockist list including "Coming Soon" stores,
  // sorted by distance. This is what the landing-page grid actually renders —
  // Coming Soon stores must appear here so customers can see them.
  let proximitySortedStores = [];
  let storesJson = storage?.getItem('allStores');
  let allStores = storesJson ? JSON.parse(storesJson) : null;
  const ipStackDataJson = storage?.getItem('ipStackData');
  const ipStackData = ipStackDataJson ? JSON.parse(ipStackDataJson) : null;
  let { state: ipStackState } = ipStackData || {};
  const optionsCount = $selectCustomOpts?.length;
  let optionHoveredIndex = -1;

  /**
   * Init
   */
  function init() {
    if (!$main) return;

    const timeout = nearestStores ? 0 : 1000;

    setTimeout(() => {
      getStores();
    }, timeout);
  }

  function getStateFromUrl() {
    const url = new URL(window.location.href);
    const { handle } = $main.dataset;
    const pathParts = url.pathname.split('/');
    const storesIndex = pathParts.indexOf(`${handle}`);
    if (storesIndex !== -1 && pathParts.length > storesIndex + 1) {
      return pathParts[storesIndex + 1];
    }
    return null;
  }

  /**
   * Add events
   */
  function addEvents() {
    // Button filter click
    if ($buttons?.length) {
      $buttons.forEach(($button) => {
        const { id } = $button;

        __.addEvent({
          id,
          event: 'click',
          fn: (e) => {
            const type = 'button';
            filterStores(type, e);
          },
        });
      });
    }

    // Dropdown filter click
    if ($dropdownOptions?.length) {
      __.addEvent({
        id: document.body,
        event: 'click',
        className: c.dom.optionsTrigger,
        fn: toggleDropdown,
      });

      $dropdownOptions.forEach(($dropdownOption) => {
        const { id } = $dropdownOption;

        __.addEvent({
          id,
          event: 'click',
          fn: (e) => {
            const type = 'dropdown';
            filterStores(type, e);
          },
        });
      });
    }
  }

  /**
   * Get Nearby Stores
   */
  function getStores() {
    // get fresh stores session data
    if (!allStores) {
      storesJson = storage?.getItem('allStores');
      allStores = storesJson ? JSON.parse(storesJson) : null;
    }

    if (!allStores) {
      console.error('no allStores data:', allStores);
      return;
    }

    fetchStoresData().then((result) => {
      if (!result) return;

      nearestStore = result.nearestStore;
      proximitySortedStores = result.stores;
      ipStackState = result.ipStackState;

      const userInStoreState = ipStackState === nearestStore?.state;
      const nearestStoresParsed = nearestStore
        ? proximitySortedStores
        : allStores.sort((storeA, storeB) => {
          if (storeA.state < storeB.state) return -1;
          if (storeA.state > storeB.state) return 1;
          return 0;
        });

      displayStores(nearestStoresParsed);
      setFilters(result.stores);
      setNearestStoreDisplay(userInStoreState);
    });
  }

  /**
   * Display stores (closest first)
   * @param {Array} stores - locations data
   */
  function displayStores(stores) {
    if (nearestStores) {
      let formattedStores = stores;
      if (nearestStores?.length && ipStackState === nearestStores[0].state) {
        const filterContainsNearestStore = stores.filter((store) => (
          store.id === nearestStores[0].id
        )).length;

        // set the closest location first if not filtered
        if (filterContainsNearestStore) {
          formattedStores = [
            nearestStores[0],
            ...stores.filter((store) => store.id !== nearestStores[0].id),
          ];
        }
      }

      buildStores(formattedStores);
    } else {
      buildStores(stores);
    }
  }

  /**
   * Build Stores
   * @param {Array} stores - locations data
   */
  function buildStores(stores) {
    const $wrapper = $main?.querySelector(`.${c.dom.wrapper}`);
    if (!$wrapper) return;

    const htmlArray = [];
    let html = '';

    stores.forEach((store) => {
      const isNearestStore = store.id === nearestStore?.id && ipStackState === nearestStore?.state;
      const storeHoursData = store.custom_fields.filter(
        (customField) => customField.name.includes('Store Hours'),
      );
      const stockistBadgeText = store.custom_fields.find(
        (customField) => customField.name.includes('Badge'),
      );

      let storeHoursText = '';
      if (storeHoursData?.length) {
        storeHoursText = parseStoreHours(storeHoursData);
      }

      const storePageUrl = `/pages/${__.handleize(store.name)}`;
      const dataWidths = __.getImageSizes(832);

      const badge = stockistBadgeText
        ? `<span class="store__grid-badge">${stockistBadgeText.value}</span>`
        : '';
      const adaBadge = stockistBadgeText
        ? `<span class="screenreader">${stockistBadgeText.value}</span>`
        : '';
      const nearestBadge = '<span class="store__grid-badge store__grid-badge--nearest">Your Nearest Store</span>';
      const adaNearestBadge = `
        <span class="screenreader">Your Nearest Store</span>
      `;
      const htmlItem = `
        <a
          class="store__grid-card"
          href="${storePageUrl}"
          title="${storePageUrl}"
        >
          <div class="store__grid-media">
            <div class="ir ir--store-grid-image is-loading store-grid__image-wrapper">
              <img
                class="lazyload store-details__image store-grid__image"
                data-widths="${dataWidths}"
                data-sizes="auto"
                tabindex="-1"
                alt=""
                data-src="${store.image_url}"
              >
            </div>
            ${isNearestStore ? nearestBadge : ''}
            ${badge}
          </div>
          <div class="store__grid-card-details">
            <div class="store__grid-card-address">
              <h3 class="store__grid-card-title">
                ${isNearestStore ? adaNearestBadge : adaBadge}
                ${store.name}
              </h3>
              <p class="store__grid-card-text">
                ${store.address_line_1}
              </p>
              <p class="store__grid-card-text">
                ${store.city}, ${store.state} ${store.postal_code}
              </p>
            </div>
            <div class="store__grid-card-info">
              <p class="store__grid-card-text">
                ${storeHoursText}
              </p>
              <p class="store__grid-card-link">
                Store Details
              </p>
            </div>
          </div>
        </a>
      `;

      htmlArray.push(htmlItem);
    });

    if (touts?.length) {
      touts.forEach((tout) => {
        let index = tout.position - 1;
        const $description = `
          <p
            class="store-landing-tout__details-description"
            style="color: ${tout.toutTextColor};"
          >
            ${tout.description}
          </p>
        `;
        const $link = `
          <a
            class="store-landing-tout__details-cta"
            href="${tout.ctaUrl}"
            title="${tout.ctaUrl}"
            style="background-color: ${tout.ctaBGColor}; color: ${tout.ctaTextColor};"
          >
            ${tout.ctaCopy}
          </a>
        `;

        if (index > htmlArray.length) index = htmlArray.length;

        const toutHtml = `
          <div
            class="store-landing-tout store__grid-card"
            style="background-color: ${tout.toutBGColor};"
            data-state-filter="${tout.stateFilter}"
          >
            <div class="ir ir--store-grid-card">
              <div class="store-landing-tout__details store__grid-card-details">
                <p
                  class="store-landing-tout__details-subheader"
                  style="color: ${tout.toutTextColor};"
                >
                  ${tout.subheader}
                </p>
                <h2
                  class="store-landing-tout__details-header"
                  style="color: ${tout.toutTextColor};"
                >
                  ${tout.header}
                </h2>
                ${tout.description ? $description : ''}
                ${tout.ctaCopy ? $link : ''}
              </div>
            </div>
          </div>
        `;

        htmlArray.splice(index, 0, toutHtml);
      });
    }

    html = htmlArray.join('');
    $wrapper.innerHTML = html.trim();
  }

  /**
   * Filter Stores
   * @param {String} filterType filter type
   * @param {Event} e event
   */
  function filterStores(filterType, e) {
    const target = e.currentTarget || e.target;
    const nearestStoresParsed = nearestStore
      ? proximitySortedStores
      : allStores.sort((storeA, storeB) => {
        if (storeA.state < storeB.state) return -1;
        if (storeA.state > storeB.state) return 1;
        return 0;
      });

    if (filterType === 'button') {
      const { stateFilter } = target.dataset;
      currentState = stateFilter;

      if (stateFilter === 'all') {
        displayStores(nearestStoresParsed);
      } else {
        const filteredStores = nearestStoresParsed
          ? nearestStoresParsed.filter((store) => store.state === stateFilter)
          : null;

        if (filteredStores?.length) {
          displayStores(filteredStores);
        }
      }
    }

    if (filterType === 'dropdown') {
      const $listItems = document.body.querySelectorAll(`.${c.dom.dropdownItem}`);
      const { text } = target.dataset;
      currentState = text;

      [...$listItems].forEach((item) => {
        __.removeClass(item, c.cls.active);
      });

      __.addClass(target, c.cls.active);
      setTimeout(() => {
        toggleDropdown(target);
      }, 150);

      $labelTextElement.innerText = text;
      __.addClass($optionsTrigger, c.cls.optionSelected);

      if (text === 'all') {
        displayStores(nearestStoresParsed);
      } else {
        const filteredStores = nearestStoresParsed
          ? nearestStoresParsed.filter((store) => store.state === text)
          : null;

        if (filteredStores?.length) {
          displayStores(filteredStores);
        }
      }
    }
    updateUrl(currentState);
  }

  /**
   * Update options and option state on toggle
   * @param {HTMLElement} currentTarget
   */
  function toggleDropdown(currentTarget) {
    $dropdownWrapper.classList.toggle(c.cls.active);
    const target = currentTarget.target || currentTarget;
    const $optionsLabelParents = __.parents(target);
    const $dropdownMenu = $optionsLabelParents
      ? $optionsLabelParents.find(($parent) => $parent.classList.contains(c.dom.dropdownMenu))
      : null;
    const $dropdownHeading = $dropdownMenu
      ? $dropdownMenu.querySelector(`.${c.dom.dropdownHeading}`)
      : null;
    const $optionsLabelTrigger = $dropdownHeading
      ? $dropdownHeading.querySelector(`.${c.dom.optionsTrigger}`)
      : null;
    const $optionsLabelIcon = $optionsLabelTrigger
      ? $optionsLabelTrigger.querySelector(`.${c.dom.optionsLabelIcon}`)
      : null;

    if (__.hasClass($dropdownWrapper, c.cls.active)) {
      $dropdownWrapper.focus();
      $optionsLabelTrigger.setAttribute('aria-expanded', 'true');
      $optionsLabelTrigger.setAttribute('aria-label', 'Close dropdown menu');
      __.addClass($optionsLabelIcon, c.cls.open);
      trapFocus($dropdownMenu);

      __.addEvent({
        id: $dropdownWrapper,
        event: 'keydown',
        fn: supportKeyboardNavigation,
      });
    } else {
      $dropdownWrapper.blur();
      $optionsLabelTrigger.focus();
      $optionsLabelTrigger.setAttribute('aria-expanded', 'false');
      $optionsLabelTrigger.setAttribute('aria-label', 'Open dropdown menu');
      __.removeClass($optionsLabelIcon, c.cls.open);

      __.removeEvent({
        id: $dropdownWrapper,
        event: 'keydown',
        fn: supportKeyboardNavigation,
      });
    }

    resetOptions();
    const $button = target.querySelector(`.${c.dom.labelButton}`);
    $button?.setAttribute('aria-pressed', 'true');
  }

  /**
   * Reset the state of all options
   */
  function resetOptions() {
    if (!$selectCustomOpts) return;

    $selectCustomOpts.forEach(($option) => {
      const $button = $option.querySelector(`.${c.dom.labelButton}`);
      $button?.setAttribute('aria-pressed', 'false');
    });
  }

  /**
   * Set keyboard accessibility for options
   * @param {object} e
   */
  function supportKeyboardNavigation(e) {
    // press down -> go next
    if (e.keyCode === 40 && optionHoveredIndex < optionsCount - 1) {
      e.preventDefault(); // prevent page scrolling
      updateCustomSelectHovered(optionHoveredIndex + 1);
    }

    // press up -> go previous
    if (e.keyCode === 38 && optionHoveredIndex > 0) {
      e.preventDefault(); // prevent page scrolling
      updateCustomSelectHovered(optionHoveredIndex - 1);
    }
    // press Enter or space -> select the option
    if (e.keyCode === 13 || e.keyCode === 32) {
      e.preventDefault();
      const option = $selectCustomOpts[optionHoveredIndex];
      if (option) {
        __.removeClass(option, c.cls.hover);
        filterStores(option);
      }
    }
  }

  /**
   * Set hover state on options
   * @param {number} newIndex
   */
  function updateCustomSelectHovered(newIndex) {
    const $prevOption = $selectCustomOpts[optionHoveredIndex];
    const $option = $selectCustomOpts[newIndex];

    if ($prevOption) {
      __.removeClass($prevOption, c.cls.hover);
    }
    if ($option) {
      __.addClass($option, c.cls.hover);
    }

    optionHoveredIndex = newIndex;
  }

  /**
   * Set Nearest Store Display
   * @param {Boolean} userInStoreState
   * @return {String} am/pm
   */
  function setNearestStoreDisplay(userInStoreState) {
    const $nearestStoreWrapper = $main.querySelector(`.${c.dom.nearestStoreWrapper}`);
    const $nearestStoreSubheaderWrapper = $main.querySelector(`.${c.dom.nearestStoreSubheaderWrapper}`);
    __.removeClass($nearestStoreSubheaderWrapper, c.cls.loading);

    const $nearestStore = $nearestStoreWrapper
      ? $nearestStoreWrapper.querySelector(`.${c.dom.nearestStoreText}`)
      : null;
    const $nearestStoreLink = $nearestStore
      ? $nearestStore.querySelector(`.${c.dom.nearestStoreLink}`)
      : null;
    const $noStoreText = $main.querySelector(`.${c.dom.noStoreText}`);
    const $noStore = $noStoreText
      ? $noStoreText.querySelector(`.${c.dom.noStore}`)
      : null;

    if (nearestStore && userInStoreState) {
      if (!$nearestStoreLink) return;

      const storeName = nearestStore.name;
      const handleizedStoreName = __.handleize(storeName);
      $nearestStoreLink.href = `/pages/${handleizedStoreName}`;
      $nearestStoreLink.title = `/pages/${handleizedStoreName}`;
      $nearestStoreLink.innerHTML = storeName;
      __.removeClass($nearestStoreWrapper, c.cls.hide);
      __.removeClass($nearestStore, c.cls.hide);
      __.addClass($noStoreText, c.cls.hide);
    } else {
      if (!$noStoreText) return;
      const storeCount = allStores?.length
        ? allStores.length
        : null;

      if ($noStore && storeCount) {
        $noStore.innerHTML = storeCount;
        __.removeClass($nearestStoreWrapper, c.cls.hide);
        __.removeClass($noStoreText, c.cls.hide);
        __.addClass($nearestStore, c.cls.hide);
      }
    }
  }

  /**
   * Set Filters
   * @param {Array} stores all stores from sessionStorage
   */
  function setFilters(stores) {
    const statesArray = [];
    if (!stores) return;

    stores.forEach((store) => {
      if (!statesArray.includes(store.state)) {
        statesArray.push(store.state);
      }
    });

    if (!statesArray?.length) return;

    buildButtonFilters(statesArray.sort());
    buildDropdownFilters(statesArray.sort());
  }

  /**
   * Build Button Filters
   * @param {Array} states array of states
   */
  function buildButtonFilters(states) {
    const numButtons = states.length + 1;
    if (!$buttonsFilter) return;

    let html = `
      <button
        class="store__navigation-button btn btn--secondary js-store-navigation-button"
        id="js-store-filter-option-all"
        data-state-filter="all"
        type="button"
        tabindex="0"
        style="width: calc(100% / ${numButtons});"
      >
        All
      </button>
    `;

    states.forEach((state) => {
      html += `
        <button
          class="store__navigation-button btn btn--secondary js-store-navigation-button"
          id="js-store-filter-option-${state}"
          data-state-filter="${state}"
          type="button"
          tabindex="0"
          style="width: calc(100% / ${numButtons});"
        >
          ${state}
        </button>
      `;
    });

    $buttonsFilter.innerHTML = html;
    $buttons = $buttonsFilter
      ? $buttonsFilter.querySelectorAll(`.${c.dom.button}`)
      : null;

    if ($buttons?.length) {
      addEvents();
      if (!stateFromUrl) return;
      const $targetButton = $buttonsFilter.querySelector(`#js-store-filter-option-${stateFromUrl.toUpperCase()}`);
      if ($targetButton) {
        $labelTextElement.innerText = stateFromUrl.toUpperCase();
        $targetButton.click();
        $targetButton.focus();
      }
    }
  }

  /**
   * Build Dropdown Filters
   * @param {Array} states array of states
   */
  function buildDropdownFilters(states) {
    let html = `
      <li
        class="dropdown-list__item js-dropdown-list-item js-store-navigation-option is-active"
        id="js-dropdown-list-item-all"
        data-option="all"
        data-text="all"
        data-value="all"
      >
        <button
          class="dropdown-list__item-btn"
          type="button"
          aria-pressed="true"
        >
          <span class="dropdown-list__item-btn-text">
            All
          </span>
        </button>
      </li>
    `;

    if (!$dropdownFilter) return;

    states.forEach((state) => {
      html += `
        <li
          class="dropdown-list__item js-dropdown-list-item js-store-navigation-option"
          id="js-dropdown-list-item-${state}"
          data-option="${state}"
          data-text="${state}"
          data-value="${state}"
        >
          <button
            class="dropdown-list__item-btn"
            type="button"
            aria-pressed="false"
          >
            <span class="dropdown-list__item-btn-text">
              ${state}
            </span>
          </button>
        </li>
      `;
    });

    $optionsDropdownList.innerHTML = html;
    $dropdownOptions = $dropdownFilter
      ? $dropdownFilter.querySelectorAll(`.${c.dom.dropdownOption}`)
      : null;

    if ($dropdownOptions?.length) {
      addEvents();
      if (!stateFromUrl) return;
      const $targetOption = $optionsDropdownList.querySelector(`#js-dropdown-list-item-${stateFromUrl.toUpperCase()}`);
      const $currentActiveOption = $dropdownFilter.querySelector(`.${c.cls.active}`);
      __.removeClass($currentActiveOption, c.cls.active);
      __.addClass($targetOption, c.cls.active);
    }
  }

  function updateUrl() {
    const { absUrl } = $main.dataset;
    window.history.replaceState({}, '', `${absUrl}/${currentState}`);
  }
  return init();
}

export default storeLandingPage;
